import React, { useEffect, useState } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "../../hooks/use-toast";
import { Pencil, Trash2, Plus, Clock } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import { Badge } from "../../components/ui/badge";
import { PincodeManager } from "./PincodeManager";

interface NonOperationalPincode {
	id: number;
	pincodes: string[];
	start_datetime: string;
	end_datetime: string;
	buffer_hours: number;
	reason: string;
	created_at: string;
	updated_by: string | null;
	is_active: boolean;
	is_delete: boolean;
}

export const NonOperationalPincodesTab: React.FC = () => {
	const [nonOperationalPincodes, setNonOperationalPincodes] = useState<
		NonOperationalPincode[]
	>([]);
	const [loading, setLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingPincode, setEditingPincode] = useState<NonOperationalPincode | null>(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [pincodeToDelete, setPincodeToDelete] = useState<NonOperationalPincode | null>(
		null,
	);
	const { toast } = useToast();
	const { user } = useAuth();

    // Changed formData.pincodes to be string[]
	const [formData, setFormData] = useState<{
        pincodes: string[];
        start_datetime: string;
        end_datetime: string;
        buffer_hours: number;
        reason: string;
    }>({
		pincodes: [],
		start_datetime: "",
		end_datetime: "",
		buffer_hours: 0,
		reason: "",
	});

	useEffect(() => {
		fetchNonOperationalPincodes();
	}, []);

	const fetchNonOperationalPincodes = async () => {
		try {
			const response = await api.getNonOperationalPincodes({
				limit: 100,
			});
			if (response.success) {
				setNonOperationalPincodes(response.data || []);
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description:
						response.error || "Failed to fetch non-operational pincodes",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to fetch non-operational pincodes",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleOpenModal = (pincodeRule?: NonOperationalPincode) => {
		if (pincodeRule) {
			setEditingPincode(pincodeRule);
			setFormData({
				pincodes: pincodeRule.pincodes, // Already an array
				start_datetime: new Date(pincodeRule.start_datetime).toISOString().slice(0, 16),
				end_datetime: new Date(pincodeRule.end_datetime).toISOString().slice(0, 16),
				buffer_hours: pincodeRule.buffer_hours,
				reason: pincodeRule.reason,
			});
		} else {
			setEditingPincode(null);
			setFormData({
				pincodes: [],
				start_datetime: "",
				end_datetime: "",
				buffer_hours: 0,
				reason: "",
			});
		}
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setEditingPincode(null);
		setFormData({
			pincodes: [],
			start_datetime: "",
			end_datetime: "",
			buffer_hours: 0,
			reason: "",
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!user?.id) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "User not authenticated",
			});
			return;
		}

		if (formData.pincodes.length === 0) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Please enter at least one pincode",
			});
			return;
		}

        // Limit validation is already in backend, but good to check here too if needed,
        // though PincodeManager also checks on Bulk Upload.
        // We'll let backend or PincodeManager handle specific warnings.

		try {
			const payload = {
				pincodes: formData.pincodes,
				start_datetime: new Date(formData.start_datetime).toISOString(),
				end_datetime: new Date(formData.end_datetime).toISOString(),
				buffer_hours: Number(formData.buffer_hours),
				reason: formData.reason,
				user_id: user.id,
			};

			const response = editingPincode
				? await api.updateNonOperationalPincode(editingPincode.id, payload)
				: await api.createNonOperationalPincode(payload);

			if (response.success) {
				toast({
					title: "Success",
					description: editingPincode
						? "Pincode stress rule updated successfully"
						: "Pincode stress rule created successfully",
				});
				handleCloseModal();
				fetchNonOperationalPincodes();
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response.error || "Failed to save pincode stress rule",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to save pincode stress rule",
			});
		}
	};

	const handleDelete = async () => {
		if (!pincodeToDelete) return;

		if (!user?.id) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "User not authenticated",
			});
			return;
		}

		try {
			const response = await api.deleteNonOperationalPincode(pincodeToDelete.id, user.id);

			if (response.success) {
				toast({
					title: "Success",
					description: "Pincode stress rule deleted successfully",
				});
				setDeleteConfirmOpen(false);
				setPincodeToDelete(null);
				fetchNonOperationalPincodes();
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: response.error || "Failed to delete pincode stress rule",
				});
			}
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Failed to delete pincode stress rule",
			});
		}
	};

	const formatDateTime = (dateString: string) => {
		return new Date(dateString).toLocaleString("en-IN", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	if (loading) {
		return <div className="p-6">Loading...</div>;
	}

	return (
		<div className="space-y-4">
			<div className="flex justify-end mb-4">
				<Button onClick={() => handleOpenModal()}>
					<Plus className="mr-2 h-4 w-4" /> Add Stress Rule
				</Button>
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Pincodes</TableHead>
						<TableHead>Start Time</TableHead>
						<TableHead>End Time</TableHead>
						<TableHead>Buffer (Hrs)</TableHead>
						<TableHead>Reason</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{nonOperationalPincodes.length === 0 ? (
						<TableRow>
							<TableCell colSpan={7} className="text-center">
								No pincode stress rules found
							</TableCell>
						</TableRow>
					) : (
						nonOperationalPincodes.map((rule) => {
							const isActive = new Date(rule.end_datetime) > new Date();
							return (
								<TableRow key={rule.id}>
									<TableCell>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleOpenModal(rule)}
                                        >
                                            View {rule.pincodes.length} Pincodes
                                        </Button>
									</TableCell>
									<TableCell>{formatDateTime(rule.start_datetime)}</TableCell>
									<TableCell>{formatDateTime(rule.end_datetime)}</TableCell>
									<TableCell>
										<div className="flex items-center">
											<Clock className="mr-2 h-4 w-4 text-gray-500" />
											{rule.buffer_hours} hrs
										</div>
									</TableCell>
									<TableCell>{rule.reason}</TableCell>
									<TableCell>
										<Badge variant={isActive ? "default" : "secondary"}>
											{isActive ? "Active" : "Expired"}
										</Badge>
									</TableCell>
									<TableCell>
										<div className="flex space-x-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={() => handleOpenModal(rule)}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => {
													setPincodeToDelete(rule);
													setDeleteConfirmOpen(true);
												}}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							);
						})
					)}
				</TableBody>
			</Table>

			{/* Add/Edit Modal */}
			<Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
				<DialogContent className="sm:max-w-[700px]">
					<DialogHeader>
						<DialogTitle>
							{editingPincode
								? "Edit Pincode Stress Rule"
								: "Add Pincode Stress Rule"}
						</DialogTitle>
						<DialogDescription>
							Configure temporary EDD buffers for specific pincodes.
						</DialogDescription>
					</DialogHeader>

					<div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md mb-4 flex items-start gap-2 text-sm text-yellow-800 dark:text-yellow-200">
						<div className="mt-0.5">⚠️</div>
						<div>
							Please do not add more than 1000 pincodes per rule.
							If you have more, please split them into multiple rules.
						</div>
					</div>

					<form onSubmit={handleSubmit}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="pincodes">Pincodes</Label>
                                <PincodeManager
                                    pincodes={formData.pincodes}
                                    onChange={(newPincodes) => setFormData({...formData, pincodes: newPincodes})}
                                />
							</div>

							<div className="grid grid-cols-2 gap-4">
								<div className="grid gap-2">
									<Label htmlFor="start_datetime">Start Date & Time</Label>
									<Input
										id="start_datetime"
										type="datetime-local"
										value={formData.start_datetime}
										onChange={(e) =>
											setFormData({
												...formData,
												start_datetime: e.target.value,
											})
										}
										required
									/>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="end_datetime">End Date & Time</Label>
									<Input
										id="end_datetime"
										type="datetime-local"
										value={formData.end_datetime}
										onChange={(e) =>
											setFormData({
												...formData,
												end_datetime: e.target.value,
											})
										}
										required
									/>
								</div>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="buffer_hours">Buffer Hours</Label>
								<Input
									id="buffer_hours"
									type="number"
									min="0"
									step="1"
									value={formData.buffer_hours}
									onChange={(e) =>
										setFormData({
											...formData,
											buffer_hours: Number(e.target.value),
										})
									}
									required
								/>
							</div>

							<div className="grid gap-2">
								<Label htmlFor="reason">Reason</Label>
								<Input
									id="reason"
									type="text"
									placeholder="e.g., Heavy Rains, Logistics Delay"
									value={formData.reason}
									onChange={(e) =>
										setFormData({
											...formData,
											reason: e.target.value,
										})
									}
									required
									maxLength={255}
								/>
							</div>
						</div>
						<DialogFooter>
							<Button type="button" variant="outline" onClick={handleCloseModal}>
								Cancel
							</Button>
							<Button type="submit">{editingPincode ? "Update" : "Create"}</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Confirm Delete</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete this pincode stress rule? This
							action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setDeleteConfirmOpen(false);
								setPincodeToDelete(null);
							}}
						>
							Cancel
						</Button>
						<Button variant="destructive" onClick={handleDelete}>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};
